import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonButton,
  IonIcon,
  IonCard,
  IonCardContent,
  IonAvatar,
  IonText,
  IonHeader,
  IonToolbar,
  IonChip,
  IonButtons,
  IonTitle
} from '@ionic/react';
import { 
  searchOutline,
  locationOutline,
  personOutline,
  mailOutline,
  callOutline,
  chevronBackOutline
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { BaseUser, Company, Contractor, Ambassador } from '../types/user';
import { useAuthContext } from '../contexts/AuthContext';

interface SearchFilters {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  city?: string;
  userType?: string;
}

export const Search: React.FC = () => {
  const { t } = useTranslation();
  const { userData: currentUser } = useAuthContext();
  const [filters, setFilters] = useState<SearchFilters>({});
  const [searchResults, setSearchResults] = useState<BaseUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const history = useHistory();

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const getUserDisplayName = (user: BaseUser): string => {
    if (user.type === 'company') {
      return (user as Company).companyName || 'Company';
    } else if (user.type === 'contractor') {
      const contractor = user as Contractor;
      return `${contractor.firstName || ''} ${contractor.lastName || ''}`.trim() || 'User';
    } else if (user.type === 'ambassador') {
      const ambassador = user as Ambassador;
      return ambassador.username || `${ambassador.firstName || ''} ${ambassador.lastName || ''}`.trim() || 'User';
    }
    return 'User';
  };

  const getUserTypeLabel = (userType: string): string => {
    switch (userType) {
      case 'company':
        return 'Entreprise';
      case 'contractor':
        return 'Prestataire';
      case 'ambassador':
        return 'Ambassadeur';
      default:
        return userType;
    }
  };

  const handleUserClick = (user: BaseUser) => {
    history.push(`/profile/${user.id}`);
  };

  const searchUsers = async () => {
    setIsSearching(true);
    
    try {
      let searchQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(100));
      
      if (filters.userType) {
        searchQuery = query(searchQuery, where('type', '==', filters.userType));
      }
      
      const querySnapshot = await getDocs(searchQuery);
      const users: BaseUser[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const userData = { id: doc.id, ...data } as BaseUser;
        
        // Filter results
        let matches = true;
        
        // Filter by first name (for contractors and ambassadors)
        if (filters.firstName) {
          if (userData.type === 'contractor' || userData.type === 'ambassador') {
            const user = userData as Contractor | Ambassador;
            matches = matches && user.firstName?.toLowerCase().includes(filters.firstName.toLowerCase());
          } else {
            matches = false;
          }
        }
        
        // Filter by last name (for contractors and ambassadors)
        if (filters.lastName) {
          if (userData.type === 'contractor' || userData.type === 'ambassador') {
            const user = userData as Contractor | Ambassador;
            matches = matches && user.lastName?.toLowerCase().includes(filters.lastName.toLowerCase());
          } else {
            matches = false;
          }
        }
        
        // Filter by company name (for companies)
        if (filters.companyName) {
          if (userData.type === 'company') {
            const company = userData as Company;
            matches = matches && company.companyName?.toLowerCase().includes(filters.companyName.toLowerCase());
          } else {
            matches = false;
          }
        }
        
        if (filters.city && userData.city) {
          matches = matches && userData.city.toLowerCase().includes(filters.city.toLowerCase());
        }
        
        if (currentUser && currentUser.id === userData.id) {
          matches = false;
        }
        
        if (matches) {
          users.push(userData);
        }
      });
      
      setSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchResults([]);
  };

  return (
    <IonPage>
      <IonContent style={{ padding: '20px', paddingTop: '100px', paddingBottom: '120px' }}>
        <div style={{ padding: '20px' }}>
          <IonCard>
            <IonCardContent>
              <IonList>
                <IonSelect
                  label="Type d'utilisateur"
                  value={filters.userType || ''}
                  onIonChange={(e) => handleFilterChange('userType', e.detail.value)}
                  placeholder="Sélectionner un type"
                >
                  <IonSelectOption value="">Tous les types</IonSelectOption>
                  <IonSelectOption value="company">Entreprise</IonSelectOption>
                  <IonSelectOption value="contractor">Prestataire</IonSelectOption>
                  <IonSelectOption value="ambassador">Ambassadeur</IonSelectOption>
                </IonSelect>

                <IonInput
                  label="Prénom"
                  value={filters.firstName || ''}
                  onIonInput={(e) => handleFilterChange('firstName', e.detail.value)}
                  placeholder="Entrer le prénom"
                />

                <IonInput
                  label="Nom"
                  value={filters.lastName || ''}
                  onIonInput={(e) => handleFilterChange('lastName', e.detail.value)}
                  placeholder="Entrer le nom"
                />

                <IonInput
                  label="Nom de l'entreprise"
                  value={filters.companyName || ''}
                  onIonInput={(e) => handleFilterChange('companyName', e.detail.value)}
                  placeholder="Entrer le nom de l'entreprise"
                />

                <IonInput
                  label="Ville"
                  value={filters.city || ''}
                  onIonInput={(e) => handleFilterChange('city', e.detail.value)}
                  placeholder="Entrer la ville"
                />
              </IonList>

              <div style={{ display: 'flex', gap: '8px', marginTop: '16px'}}>
                <button
                  style={{margin:"15px"}}
                  onClick={clearFilters}
                  disabled={!filters.firstName && !filters.lastName && !filters.companyName && !filters.city && !filters.userType}
                >
                  Effacer 
                </button>
                <IonButton
                  expand="block"
                  onClick={searchUsers}
                  disabled={isSearching}
                >
                  {isSearching ? 'Recherche...' : 'Rechercher'}
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>

          {searchResults.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <IonText color="medium">
                <h3 style={{ margin: '16px 0', textAlign: 'center' }}>
                  {searchResults.length} résultats trouvés
                </h3>
              </IonText>

              <IonList>
                {searchResults.map((user, index) => (
                  <IonCard 
                    key={user.id} 
                    button 
                    onClick={() => handleUserClick(user)}
                    style={{ 
                      marginBottom: index === searchResults.length - 1 ? '60px' : '8px',
                      marginTop: index === 0 ? '0' : '8px'
                    }}
                  >
                    <IonCardContent>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <IonAvatar style={{ width: '50px', height: '50px' }}>
                          {user.profilePhotoUrl ? (
                            <img src={user.profilePhotoUrl} alt={getUserDisplayName(user)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <IonIcon icon={personOutline} style={{ fontSize: '24px', color: '#666' }} />
                          )}
                        </IonAvatar>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>
                            {getUserDisplayName(user)}
                          </h3>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                            <div color="primary" style={{ fontSize: '10px', height: '20px' }}>
                              {getUserTypeLabel(user.type)}
                            </div>
                            {user.city && (
                              <div color="medium" style={{ fontSize: '10px', marginLeft:'10px' ,height: '20px' }}>
                                {user.city}
                              </div>
                            )}
                          </div>
                          {user.email && (
                            <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center' }}>
                              <IonIcon icon={mailOutline} style={{ fontSize: '10px', marginRight: '4px' }} />
                              {user.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </IonCardContent>
                  </IonCard>
                ))}
              </IonList>
            </div>
          )}

          {searchResults.length === 0 && (filters.firstName || filters.lastName || filters.companyName || filters.city || filters.userType) && !isSearching && (
            <IonCard style={{ marginTop: '16px' }}>
              <IonCardContent style={{ textAlign: 'center', padding: '24px' }}>
                <IonIcon icon={searchOutline} size="large" color="medium" />
                <IonText color="medium">
                  <p style={{ marginTop: '8px' }}>Aucun résultat trouvé</p>
                </IonText>
              </IonCardContent>
            </IonCard>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Search;